import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AppHeader, Button, Dialog, IconButton, Loader } from '@origo/origo-ui';
import { Toaster } from '@origo/origo-components';
import { FloppyDiskIcon, QuestionIcon, CheckCircleIcon, WarningCircleIcon, XIcon } from '@phosphor-icons/react';
import { type Arealplan, useArealplanGeometry } from '@origo/arealplan';
import type { PlanMetadata } from '../services/PlanMetadataService';
import type { Område, OmrådeParentType } from '@origo/seksjon';
import {
    useDelområdegrupper,
    useCreateDelområdegruppe,
    useAddDelområdegruppeMedlemmer,
    useRemoveDelområdegruppeMedlemmer,
    useRenameDelområdegruppe,
    useDeleteDelområdegruppe,
    useDeleteSeksjon,
} from '@origo/seksjon';
import { useMapViewContext, selectedPolygonSymbol } from '@origo/map-context';
import Graphic from '@arcgis/core/Graphic';
import Polygon from '@arcgis/core/geometry/Polygon';
import { contextId, type SeksjonContext } from '../types';
import { findFeaturesForContext } from '../findFeaturesForContext';
import { useNavigation } from '../hooks/useNavigation';
import { useAdminAreas } from '../hooks/useAdminAreas';
import { useAdminSections } from '../hooks/useAdminSections';
import { useDelomradeContextMapPick } from '../hooks/useDelomradeContextMapPick';
import { NavPanel, type DelområdegruppeView, type OmrådeDragTarget } from '../components/NavPanel';
import { useCopyVeiledninger } from '../hooks/useCopyVeiledninger';
import { SectionList } from '../components/SectionList';
import { DelomradeAdmin } from '../components/DelomradeAdmin';
import { EgendefinerteOmraderAdmin } from '../components/EgendefinerteOmraderAdmin';
import { useOmråder } from '../../planer/hooks/useOmråder';
import { useAppDispatch, useAppSelector } from '../../../store/storeHooks';
import { selectExcludedDelomradetyper, setExcluded } from '../../../store/delomradetypeFilterSlice';
import { setEiendomsvelgerFocus } from '../../../store/uiSlice';
import { EiendomsvelgerPanel } from '../../eiendommer/EiendomsvelgerPanel';
import { buildEiendomsvelgerTarget } from '../../eiendommer/eiendomsvelgerTarget';
import { DelomradetypeFilterPanel } from '../components/DelomradetypeFilterPanel';
import { HelpSheet } from '../components/HelpSheet';
import { OmrådeEditorPanel, type ConvertedTarget, type MovedTarget } from '../components/OmrådeEditorPanel';
import { GroupEditorPanel, type GroupEditorInitial, type GroupEditorSubmit } from '../components/GroupEditorPanel';
import { InstillingerView } from '../components/Instillinger-view/InstillingerView';
import { PLANNIVA_STANDARDBOKSER } from './defaultSections';
import styles from './AdminPage.module.css';

interface Props {
    plan: Arealplan;
    onBack: () => void;
    onClose: () => void;
    onPublish?: (planId: string, kommunenummer?: number) => Promise<void>;
    onUnpublish?: (planId: string) => Promise<void>;
    onEdited?: (planId: string, kommunenummer?: number, seksjonCountDelta?: number) => void;
    onUpdateBeskrivelse?: (beskrivelse: string) => Promise<void>;
    onUpdatePlanregisterlink?: (url: string) => Promise<void>;
    onUpdateGjeldendeBestemmelser?: (url: string) => Promise<void>;
    eiendommerUrl?: string;
    isPublished?: boolean;
    sistRedigertDato?: Date | null;
    planMetadata?: PlanMetadata;
}

function contextLabel(
    ctx: SeksjonContext,
    areas: Område[],
    grupper: readonly DelområdegruppeView[] = []
): string {
    if (ctx.type === 'kommune') return 'Veiledning for hele kommunen';
    if (ctx.type === 'plan') return 'Planens veiledninger';
    if (ctx.type === 'delomradetype') {
        const area = areas.find(a => a.id === ctx.delomradetype);
        return area?.navn ?? (ctx.delomradetype ?? '');
    }
    if (ctx.type === 'delomradegruppe') {
        const g = grupper.find((x) => x.id === ctx.gruppeId);
        return g?.navn ?? 'Delområdegruppe';
    }
    if (ctx.type === 'delomrade') {
        for (const area of areas) {
            const child = area.children?.find(c => c.id === ctx.omradeId);
            if (child) return child.navn;
        }
        return ctx.omradeId ?? '';
    }
    if (ctx.type === 'omrade') return ctx.omradeNavn ?? ctx.omradeId ?? '';
    return ctx.type;
}

function contextCode(ctx: SeksjonContext, areas: Område[]): string | undefined {
    if (ctx.type === 'delomradetype') {
        return areas.find(a => a.id === ctx.delomradetype)?.code;
    }
    if (ctx.type === 'delomrade') {
        for (const area of areas) {
            const child = area.children?.find(c => c.id === ctx.omradeId);
            if (child?.code) return child.code;
        }
    }
    return undefined;
}

function contextSubtitle(ctx: SeksjonContext, planNavn: string, areaLabel?: string, childCount?: number, gruppeMemberCount?: number): string {
    if (ctx.type === 'kommune') return 'Veiledningspunkter som vises til innbygger for hele kommunen.';
    if (ctx.type === 'plan') return `Her ser du alle veiledningspunktene for planen \u00ab${planNavn}\u00bb. Du ser hvor hver veiledning kommer fra, og kan redigere de direkte herfra.`;
    if (ctx.type === 'delomradetype' && areaLabel && childCount) {
        return `${childCount} ${areaLabel.toLowerCase()}-områder i planen. Veiledning du legger til her gjelder alle.`;
    }
    if (ctx.type === 'delomradegruppe') {
        const n = gruppeMemberCount ?? 0;
        return `${n} delområde${n === 1 ? '' : 'r'} i denne gruppen. Veiledning du legger til her gjelder alle medlemmer.`;
    }
    if (ctx.type === 'delomrade') {
        return 'Veiledning spesifikt for dette ene området.';
    }
    return 'Veiledning som vises til innbygger for dette området.';
}

export function AdminPage(props: Readonly<Props>) {
    return <AdminPageInner {...props} />;
}

function AdminPageInner({ plan, onBack, onClose, onPublish, onUnpublish, onEdited, onUpdateBeskrivelse, onUpdatePlanregisterlink, onUpdateGjeldendeBestemmelser, eiendommerUrl, isPublished, sistRedigertDato, planMetadata }: Readonly<Props>) {
    const { t } = useTranslation();
    const planId = plan.planId;
    const planNavn = plan.planNavn ?? '';
    // KOMMUNENAVN finnes ikke som standard plan-list-felt — dette feltet er
    // uvist i Arealplan; settes til tom for nå.
    const kommune = '';

    const dispatch = useAppDispatch();
    const { context, setContext } = useNavigation(planId);
    const { mapView } = useMapViewContext();
    // Hent delområdene i kartets SR så de kan rendres/hit-testes uten klient-
    // side reprojisering. Til kartet er klart er wkid undefined → useAdminAreas
    // holder queryen disabled og viser lasting.
    const mapWkid = mapView?.spatialReference?.wkid ?? undefined;
    const { areas, planomrader, loading: areasLoading } = useAdminAreas(plan, mapWkid);
    // Plangeometrien (RpOmråde) hentes på etterspørsel og brukes som grunnsett
    // for eiendomsvelgeren på plan-nivå.
    const planGeometryQuery = useArealplanGeometry(plan);
    const { områder: egendefinerte, updateOmråde } = useOmråder(planId);
    const deleteSeksjon = useDeleteSeksjon();
    const kommunenummerNum = plan.kommunenummer ? Number(plan.kommunenummer) || undefined : undefined;
    const handleEdited = useCallback((seksjonCountDelta?: number) => {
        onEdited?.(planId, kommunenummerNum, seksjonCountDelta);
    }, [onEdited, planId, kommunenummerNum]);

    const handlePublishAfterSave = useCallback(async () => {
        if (!onPublish) return;
        await onPublish(planId, kommunenummerNum);
    }, [onPublish, planId, kommunenummerNum]);

    const adminSections = useAdminSections(context, { egendefinerte, areas, onEdited: handleEdited });

    const withPublish = useCallback(
        <T extends unknown[]>(fn: (...args: T) => Promise<void>) =>
            async (...args: T): Promise<void> => {
                await fn(...args);
                if (isPublished) handlePublishAfterSave().catch(console.error);
            },
        [isPublished, handlePublishAfterSave]
    );

    const handleReorder = useMemo(() => withPublish(adminSections.reorder), [withPublish, adminSections.reorder]);
    const handleHideSection = useMemo(() => withPublish(adminSections.hideSection), [withPublish, adminSections.hideSection]);
    const handleUnhideSection = useMemo(() => withPublish(adminSections.unhideSection), [withPublish, adminSections.unhideSection]);
    const handleToggleOverride = useMemo(() => withPublish(adminSections.toggleOverride), [withPublish, adminSections.toggleOverride]);

    const excluded = useAppSelector(selectExcludedDelomradetyper);

    const DEFAULT_BESKRIVELSE = 'Dette er et forenklet bilde av hva som gjelder for eiendommen din, basert på gjeldende regulering. Den fullstendige og juridisk bindende informasjonen finner du her:';
    const instillingerSynced = useRef(false);
    const [instillingerBeskrivelse, setInstillingerBeskrivelse] = useState(planMetadata?.PlanensBeskrivelse ?? DEFAULT_BESKRIVELSE);
    const [instillingerPlanregisterlink, setInstillingerPlanregisterlink] = useState(planMetadata?.Planregisterlink ?? '');
    const [instillingerGjeldendeBestemmelser, setInstillingerGjeldendeBestemmelser] = useState(planMetadata?.GjeldendeBestemmelser ?? '');


    useEffect(() => {
        if (instillingerSynced.current || planMetadata === undefined) return;
        instillingerSynced.current = true;
        setInstillingerBeskrivelse(planMetadata.PlanensBeskrivelse ?? DEFAULT_BESKRIVELSE);
        setInstillingerPlanregisterlink(planMetadata.Planregisterlink ?? '');
        setInstillingerGjeldendeBestemmelser(planMetadata.GjeldendeBestemmelser ?? '');
    }, [planMetadata]);

    const [filterOpen, setFilterOpen] = useState(false);
    const [showHelp, setShowHelp] = useState(false);
    const [editingOmråde, setEditingOmråde] = useState<Område | null>(null);
    const [creatingOmråde, setCreatingOmråde] = useState(false);
    const [creatingParent, setCreatingParent] = useState<{ parentType: OmrådeParentType; parentId: string } | null>(null);
    const [groupEditor, setGroupEditor] = useState<GroupEditorInitial | null>(null);
    const [groupEditorError, setGroupEditorError] = useState<string | null>(null);
    const [eiendomvelgerOpen, setEiendomvelgerOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveAlert, setSaveAlert] = useState<'success' | 'error' | null>(null);
    const [saveAlertMessage, setSaveAlertMessage] = useState('Planen er publisert.');
    const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);
    const [unpublishing, setUnpublishing] = useState(false);
    const { execute: executeCopyVeiledninger, copyResult, clearResult: clearCopyResult } = useCopyVeiledninger(planId);
    const [dragBanner, setDragBanner] = useState<{ message: string; onUndo: () => void } | null>(null);

    const handleNavMoveOmråde = useCallback(async (omrade: Område, target: OmrådeDragTarget) => {
        if (omrade.objectId == null) return;
        const oldParentType = omrade.parentType;
        const oldParentId = omrade.parentId;
        await updateOmråde(omrade.objectId, omrade.navn, {
            parentType: target.parentType,
            parentId: target.parentId,
        });
        setContext({ type: 'omrade', planId, omradeId: omrade.id, omradeNavn: omrade.navn });
        if (isPublished) handlePublishAfterSave().catch(console.error);
        setDragBanner({
            message: `Området ble flyttet inn under "${target.navn}"`,
            onUndo: () => {
                if (omrade.objectId == null || oldParentType == null || oldParentId == null) return;
                updateOmråde(omrade.objectId, omrade.navn, {
                    parentType: oldParentType,
                    parentId: oldParentId,
                }).catch(console.error);
            },
        });
    }, [updateOmråde, setContext, planId, isPublished, handlePublishAfterSave]);

    const handleNavCopyVeiledninger = useCallback((omrade: Område, target: OmrådeDragTarget) => {
        executeCopyVeiledninger(omrade, target);
    }, [executeCopyVeiledninger]);

    useEffect(() => {
        if (!copyResult) return;
        if (copyResult.error) {
            console.error('[DragCopy]', copyResult.error);
            setSaveAlertMessage('Kopieringen feilet. Prøv via Rediger-panelet.');
            setSaveAlert('error');
            clearCopyResult();
            return;
        }
        if (copyResult.result) {
            handleOmrådeConverted(copyResult.result);
            if (isPublished) handlePublishAfterSave().catch(console.error);
            clearCopyResult();
        }
    }, [copyResult]); // eslint-disable-line react-hooks/exhaustive-deps

    const addSeksjonRef = useRef(adminSections.addSeksjon);
    addSeksjonRef.current = adminSections.addSeksjon;

    const defaultsAddedForPlan = useRef<string | null>(null);

    useEffect(() => {
        if (
            context.type !== 'plan' ||
            adminSections.loading ||
            adminSections.seksjoner.some(s => !s.isInherited && !s.kommunal) ||
            defaultsAddedForPlan.current === planId
        ) return;
        defaultsAddedForPlan.current = planId;
        const addFn = addSeksjonRef.current;
        const startPriority = adminSections.seksjoner.length;
        (async () => {
            for (let i = 0; i < PLANNIVA_STANDARDBOKSER.length; i++) {
                const boks = PLANNIVA_STANDARDBOKSER[i];
                await addFn(
                    { tittel: boks.tittel, tekst: boks.tekst, kommunal: false },
                    { synlig: false, prioritet: startPriority + i }
                );
            }
        })().catch(console.error);
    }, [context.type, adminSections.loading, adminSections.seksjoner.length, planId]);

    const [importedSections, setImportedSections] = useState<{
        targetContextId: string;
        globalIds: ReadonlySet<string>;
        objectIds: number[];
    } | null>(null);

    // ── Delområdegrupper ──
    // Hentes her fordi NavPanel også trenger gruppe-listen for hierarki-rendering;
    // CRUD-handlinger ligger i <DelomradeAdmin>.
    const { data: gruppeData } = useDelområdegrupper(planId);

    const gruppeViews = useMemo<DelområdegruppeView[]>(() => {
        if (!gruppeData) return [];
        const memberByGroup = new Map<string, string[]>();
        for (const m of gruppeData.medlemmer) {
            if (!m.parentId || !m.code) continue;
            if (!memberByGroup.has(m.parentId)) memberByGroup.set(m.parentId, []);
            memberByGroup.get(m.parentId)!.push(m.code);
        }
        return gruppeData.grupper.map((g) => ({
            id: g.id,
            objectId: g.objectId ?? -1,
            navn: g.navn,
            delomradetype: g.parentId ?? '',
            memberDelomradeIds: memberByGroup.get(g.id) ?? [],
        }));
    }, [gruppeData]);

    const excludedSet = useMemo(() => new Set(excluded), [excluded]);

    const filteredAreas = useMemo<Område[]>(
        () => areas.filter((a) => !excludedSet.has(a.id)),
        [areas, excludedSet]
    );

    // Hvor mange av DENNE planens delområdetyper som er skjult. Globalt
    // ekskludert-sett kan inneholde IDer fra andre planer; chip-telleren
    // skal kun reflektere det som faktisk er filtrert ut her.
    const hiddenInPlan = areas.length - filteredAreas.length;

    const handleClearPlanFilter = useCallback(() => {
        const planIdSet = new Set(areas.map((a) => a.id));
        dispatch(setExcluded(excluded.filter((id) => !planIdSet.has(id))));
    }, [areas, excluded, dispatch]);

    // ── Highlight selected delområde on the map ──
    // When the caseworker clicks a tree item, the corresponding polygons get
    // a persistent accent-colored outline + goTo so they see exactly which
    // part of the plan area they're writing guidance for. Cleared when
    // navigating back to the plan overview.
    // «Planens veiledninger» valgt før plangeometrien er hentet — markeres når
    // den kommer (se effekten under highlightContext).
    const pendingPlanHighlightRef = useRef(false);

    const highlightContext = useCallback((ctx: SeksjonContext) => {
        if (!mapView) return;
        mapView.graphics.removeAll();
        pendingPlanHighlightRef.current = ctx.type === 'plan' && !planGeometryQuery.data;

        // For en delområdegruppe slår vi opp medlemmenes delområde-IDer slik at
        // findFeaturesForContext kan markere alle gruppens delområder i kartet.
        const gruppeMembers = ctx.type === 'delomradegruppe' && ctx.gruppeId
            ? new Set(gruppeViews.find((g) => g.id === ctx.gruppeId)?.memberDelomradeIds ?? [])
            : undefined;

        // «Planens veiledninger» gjelder hele planen — marker planområdet.
        const geometries: unknown[] = ctx.type === 'plan'
            ? [planGeometryQuery.data]
            : findFeaturesForContext(ctx, planomrader, gruppeMembers).map((f) => f.geometri);

        const polygons = geometries
            .filter(Boolean)
            .map((g: any) => (g.type ? (g as __esri.Polygon) : Polygon.fromJSON(g)));
        if (polygons.length === 0) return;

        const symbol = selectedPolygonSymbol();
        // addMany i ett kall — ikke .add() i loop. Hver enkelt add() trigger en
        // egen render-runde på grafikk-laget; for en delområdetype med hundrevis
        // av delområder (kommuneplan) fryser det hovedtråden. addMany gir én pass.
        mapView.graphics.addMany(
            polygons.map((p) => new Graphic({ geometry: p, symbol: symbol as never }))
        );
        // goTo er asynk og kan trygt fyres uten await — men avbrutt navigasjon
        // (rask veksling mellom kontekster) kaster AbortError, så vi svelger den.
        mapView.goTo(polygons, { duration: 400, easing: 'ease-out' }).catch(() => { /* avbrutt navigasjon */ });
    }, [mapView, planomrader, gruppeViews, planGeometryQuery.data]);

    useEffect(() => {
        if (!pendingPlanHighlightRef.current || !planGeometryQuery.data) return;
        if (context.type === 'plan') highlightContext(context);
        else pendingPlanHighlightRef.current = false;
    }, [planGeometryQuery.data, context, highlightContext]);

    const handleSelect = useCallback((ctx: SeksjonContext) => {
        if (importedSections && contextId(ctx) !== importedSections.targetContextId) {
            setImportedSections(null);
        }
        setContext(ctx);
        highlightContext(ctx);
        // Eiendomsvelgeren er knyttet til ett nivå om gangen — lukk den ved
        // navigasjon, brukeren åpner den igjen via «Rediger eiendommer».
        setEiendomvelgerOpen(false);
    }, [setContext, highlightContext, importedSections, setImportedSections]);

    // ── Kartvelger for delområde ──
    // Når brukeren står på en delområdetype, lar vi dem klikke i kartet for å
    // velge det delområdet klikket treffer. Mange delområder deler navn og er
    // umulige å skille i listen; kart-klikk gir et entydig valg. Bare aktiv når
    // ingen editor-panel er åpent (de eier sine egne kart-interaksjoner).
    const noPanelOpen =
        !groupEditor && !editingOmråde && !creatingOmråde && !eiendomvelgerOpen;
    const delomradePickCandidates = useMemo(
        () =>
            (context.type === 'delomradetype' && context.delomradetype
                ? filteredAreas.find((a) => a.id === context.delomradetype)?.children
                : undefined
            )?.map((c) => ({
                id: c.id,
                navn: c.navn,
                geometry: c.geometry as __esri.Polygon | undefined,
            })) ?? [],
        [context.type, context.delomradetype, filteredAreas]
    );
    useDelomradeContextMapPick({
        mapView,
        enabled: context.type === 'delomradetype' && noPanelOpen,
        candidates: delomradePickCandidates,
        onPick: (omradeId, omradeNavn) => {
            handleSelect({
                type: 'delomrade',
                planId,
                omradeId,
                omradeNavn,
                delomradetype: context.delomradetype,
            });
        },
    });


    // Mål for eiendomsvelgeren utledet fra aktiv kontekst. `null` på nivåer uten
    // eiendomsvelger (delområdetype/kommune/admin). Plangeometrien kommer fra
    // useArealplanGeometry; gruppe-geometri unioneres fra medlemsdelområdene.
    const eiendomvelgerTarget = useMemo(
        () => buildEiendomsvelgerTarget({
            context,
            planId,
            planGeometryJson: planGeometryQuery.data ?? null,
            planomrader,
            egendefinerte,
            gruppeViews,
        }),
        [context, planId, planGeometryQuery.data, planomrader, egendefinerte, gruppeViews]
    );

    // Map-first fokus-modus: når eiendomsvelgeren er åpen, kringkast så
    // AppLayout/AdminSidePanel kan skjule adressesøk + nav-rail og låse 50/50.
    useEffect(() => {
        dispatch(setEiendomsvelgerFocus(eiendomvelgerOpen));
        return () => { dispatch(setEiendomsvelgerFocus(false)); };
    }, [eiendomvelgerOpen, dispatch]);

    // Info about the active delomradetype — child count + label for
    // human-readable subtitle text.
    const activeAreaInfo = useMemo(() => {
        if (context.type !== 'delomradetype' || !context.delomradetype) return null;
        const area = filteredAreas.find(a => a.id === context.delomradetype);
        if (!area) return null;
        return { label: area.navn, childCount: area.children?.length ?? 0 };
    }, [context, filteredAreas]);

    const sistEndretTid = sistRedigertDato?.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
    const sistEndretKort = sistRedigertDato
        ? t('admin.adminPage.dateTime', {
            date: sistRedigertDato.toLocaleDateString('nb-NO', { day: 'numeric', month: 'numeric', year: 'numeric' }),
            time: sistEndretTid,
        })
        : '';

    const handleBack = useCallback(() => {
        setEiendomvelgerOpen(false);
        onBack();
    }, [onBack]);

    const handleSave = useCallback(async () => {
        if (!onPublish) return;
        setSaving(true);
        setSaveAlert(null);
        try {
            const kommunenummer = plan.kommunenummer ? Number(plan.kommunenummer) || undefined : undefined;
            await onPublish(planId, kommunenummer);
            setSaveAlertMessage('Planen er publisert.');
            setSaveAlert('success');
        } catch (err) {
            console.error('[AdminPage] publishPlan failed:', err);
            setSaveAlert('error');
        } finally {
            setSaving(false);
        }
    }, [onPublish, planId, plan.kommunenummer]);

    const handleUnpublish = useCallback(async () => {
        if (!onUnpublish) return;
        setUnpublishing(true);
        try {
            await onUnpublish(planId);
            setUnpublishDialogOpen(false);
            setSaveAlertMessage('Planen er avpublisert.');
            setSaveAlert('success');
        } catch (err) {
            console.error('[AdminPage] unpublishPlan failed:', err);
            setSaveAlert('error');
        } finally {
            setUnpublishing(false);
        }
    }, [onUnpublish, planId]);

    const openOmrådeEditorForCreate = useCallback((parent?: { parentType: OmrådeParentType; parentId: string }) => {
        setEiendomvelgerOpen(false);
        setCreatingParent(parent ?? null);
        setCreatingOmråde(true);
    }, []);

    /**
     * Snarvei fra SectionList når brukeren står på et delområde/egendefinert
     * område: nytt område nestes under aktiv kontekst som forelder.
     */
    const openOmrådeEditorAsChild = useCallback(() => {
        if (context.type === 'delomrade' && context.omradeId) {
            openOmrådeEditorForCreate({ parentType: 'Delområde', parentId: context.omradeId });
        } else if (context.type === 'omrade' && context.omradeId) {
            openOmrådeEditorForCreate({ parentType: 'Område', parentId: context.omradeId });
        }
    }, [context, openOmrådeEditorForCreate]);

    const openOmrådeEditorForEdit = useCallback((item: Område) => {
        setEiendomvelgerOpen(false);
        setEditingOmråde(item);
    }, []);

    const closeOmrådeEditor = useCallback(() => {
        setCreatingOmråde(false);
        setCreatingParent(null);
        setEditingOmråde(null);
    }, []);

    /** Naviger brukeren til target delomr/gruppe etter en vellykket kopiering. */
    const handleOmrådeConverted = useCallback((target: ConvertedTarget) => {
        const targetCtx: SeksjonContext = target.kind === 'gruppe'
            ? {
                type: 'delomradegruppe',
                planId,
                gruppeId: target.gruppeId,
                delomradetype: target.delomradetype,
            }
            : target.kind === 'delomradetype'
            ? {
                type: 'delomradetype',
                planId,
                delomradetype: target.id,
            }
            : {
                type: 'delomrade',
                planId,
                omradeId: target.id,
                omradeNavn: target.navn,
                delomradetype: target.delomradetype,
            };
        setContext(targetCtx);
        const tCtxId = contextId(targetCtx);
        const objectIds = target.copiedFrom.copiedObjectIds;
        setImportedSections({
            targetContextId: tCtxId,
            globalIds: new Set(target.copiedFrom.copiedGlobalIds),
            objectIds,
        });
        if (isPublished) handlePublishAfterSave().catch(console.error);
        const count = target.copiedFrom.sectionCount;
        setDragBanner({
            message: `${count} veiledningspunkt${count === 1 ? '' : 'er'} flyttet til "${target.navn}". Husk å slett veiledningspunktene i det egendefinerte området.`,
            onUndo: () => {
                setImportedSections(null);
                Promise.all(objectIds.map((id) => deleteSeksjon.mutateAsync({ objectId: id }))).catch(console.error);
            },
        });
    }, [setContext, planId, isPublished, handlePublishAfterSave, deleteSeksjon]);

    /** Naviger brukeren til det flyttede området etter en vellykket flytt. */
    const handleOmrådeFlyttet = useCallback((_target: MovedTarget) => {
        if (editingOmråde) {
            setContext({ type: 'omrade', planId, omradeId: editingOmråde.id, omradeNavn: editingOmråde.navn });
        }
        if (isPublished) handlePublishAfterSave().catch(console.error);
    }, [setContext, planId, editingOmråde, isPublished, handlePublishAfterSave]);

    const områdeEditorOpen = creatingOmråde || editingOmråde !== null;
    const groupEditorOpen = groupEditor !== null;

    /** Hurtigvalg fra SectionList: opprett delområdegruppe i aktiv delområdetype. */
    const createGruppeMutation = useCreateDelområdegruppe();
    const renameGruppeMutation = useRenameDelområdegruppe();
    const addMembersMutation = useAddDelområdegruppeMedlemmer();
    const removeMembersMutation = useRemoveDelområdegruppeMedlemmer();
    const deleteGruppeMutation = useDeleteDelområdegruppe();

    const blockedMembersByType = useMemo<Record<string, string[]>>(() => {
        const out: Record<string, string[]> = {};
        for (const g of gruppeViews) {
            if (!out[g.delomradetype]) out[g.delomradetype] = [];
            out[g.delomradetype].push(...g.memberDelomradeIds);
        }
        return out;
    }, [gruppeViews]);

    // Skjul AppLayouts Eiendommer-bottompanel mens gruppe-editoren er åpen —
    // editoren styrer sine egne kartlag for delområde-seleksjon.
    const openGroupEditor = useCallback((initial: GroupEditorInitial) => {
        setEiendomvelgerOpen(false);
        setGroupEditorError(null);
        setGroupEditor(initial);
    }, []);

    /** Hurtigvalg fra SectionList: opprett delområdegruppe i aktiv delområdetype. */
    const handleAddGroup = useCallback(() => {
        if (context.type !== 'delomradetype' || !context.delomradetype) return;
        openGroupEditor({ delomradetype: context.delomradetype });
    }, [context, openGroupEditor]);

    /** Fra DelomradeAdmin-visningen: opprett gruppe uten forhåndsvalgt type. */
    const handleNewGroupGeneric = useCallback(() => {
        openGroupEditor({});
    }, [openGroupEditor]);

    /** Fra DelomradeAdmin-visningen: rediger en valgt gruppe. */
    const handleEditGroupFromList = useCallback((g: DelområdegruppeView) => {
        openGroupEditor({
            gruppe: { objectId: g.objectId, id: g.id, navn: g.navn },
            delomradetype: g.delomradetype,
        });
    }, [openGroupEditor]);

    /** Lukk gruppe-editoren og gjenopprett bottompanel-seleksjon for aktiv kontekst. */
    const closeGroupEditor = useCallback(() => {
        setGroupEditor(null);
        setGroupEditorError(null);
    }, []);

    const groupCurrentMembers = useMemo<readonly string[]>(() => {
        if (!groupEditor?.gruppe) return [];
        return gruppeViews.find((g) => g.id === groupEditor.gruppe!.id)?.memberDelomradeIds ?? [];
    }, [groupEditor, gruppeViews]);

    /** Hurtigvalg fra SectionList: rediger aktivt egendefinert område / delområdegruppe. */
    const handleEditActiveContext = useCallback(() => {
        if (context.type === 'omrade' && context.omradeId) {
            const item = egendefinerte.find((o) => o.id === context.omradeId);
            if (item) openOmrådeEditorForEdit(item);
        } else if (context.type === 'delomradegruppe' && context.gruppeId) {
            const gruppe = gruppeViews.find((g) => g.id === context.gruppeId);
            if (gruppe) {
                openGroupEditor({
                    gruppe: { objectId: gruppe.objectId, id: gruppe.id, navn: gruppe.navn },
                    delomradetype: gruppe.delomradetype,
                });
            }
        }
    }, [context, egendefinerte, gruppeViews, openOmrådeEditorForEdit, openGroupEditor]);

    const canEditActiveContext =
        (context.type === 'omrade' && !!context.omradeId) ||
        (context.type === 'delomradegruppe' && !!context.gruppeId);

    const handleGroupSubmit = useCallback(async (values: GroupEditorSubmit) => {
        setGroupEditorError(null);
        try {
            if (groupEditor?.gruppe) {
                if (values.navn !== groupEditor.gruppe.navn) {
                    await renameGruppeMutation.mutateAsync({
                        objectId: groupEditor.gruppe.objectId,
                        id: groupEditor.gruppe.id,
                        planId,
                        navn: values.navn,
                    });
                }
                if (values.addMembers.length > 0) {
                    await addMembersMutation.mutateAsync({
                        planId,
                        gruppeId: groupEditor.gruppe.id,
                        delområdeIds: values.addMembers,
                    });
                }
                if (values.removeMembers.length > 0) {
                    await removeMembersMutation.mutateAsync({
                        planId,
                        gruppeId: groupEditor.gruppe.id,
                        delområdeIds: values.removeMembers,
                    });
                }
            } else {
                await createGruppeMutation.mutateAsync({
                    planId,
                    delområdetype: values.delomradetype,
                    navn: values.navn,
                    memberDelområdeIds: values.addMembers,
                });
            }
            closeGroupEditor();
        } catch (err) {
            setGroupEditorError((err as Error)?.message ?? 'Kunne ikke lagre gruppen');
        }
    }, [groupEditor, planId, createGruppeMutation, renameGruppeMutation, addMembersMutation, removeMembersMutation, closeGroupEditor]);

    const handleGroupDelete = useCallback(async () => {
        if (!groupEditor?.gruppe) return;
        setGroupEditorError(null);
        try {
            await deleteGruppeMutation.mutateAsync({
                planId,
                gruppe: { id: groupEditor.gruppe.id, objectId: groupEditor.gruppe.objectId },
            });
            // Fall tilbake til delområdetype-konteksten (gruppen finnes ikke lenger).
            const delomradetype = groupEditor.delomradetype;
            closeGroupEditor();
            if (delomradetype) {
                setContext({ type: 'delomradetype', planId, delomradetype });
            }
        } catch (err) {
            setGroupEditorError((err as Error)?.message ?? 'Kunne ikke slette gruppen');
        }
    }, [groupEditor, planId, deleteGruppeMutation, setContext, closeGroupEditor]);

    void onClose;

    return (
        <div className={styles.root}>
            <AppHeader
                className={styles.header}
                title={eiendomvelgerOpen ? '' : planNavn}
                onBack={eiendomvelgerOpen ? () => setEiendomvelgerOpen(false) : handleBack}
                backLabel={eiendomvelgerOpen ? 'Tilbake' : 'Søk'}
                subtitle={!eiendomvelgerOpen && planId ? t('admin.adminPage.planId', { planId }) : undefined}
                actions={
                    <>
                        <IconButton
                            icon={<QuestionIcon size={18} />}
                            label="Hjelp — slik fungerer veiledningssystemet"
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowHelp(true)}
                        />
                        {!eiendomvelgerOpen && (isPublished ? (
                            <>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setUnpublishDialogOpen(true)}
                                    disabled={saving}
                                >
                                    Avpubliser
                                </Button>
                                <span className={styles.autoPublishStatus}>
                                    <span className={styles.autoPublishStatusRow}>
                                        {saving && <Loader size="sm" inline label="Publiserer…" />}
                                        <span>{saving ? t('admin.adminPage.publishing') : t('admin.adminPage.autoPublish')}</span>
                                    </span>
                                    {sistRedigertDato && !saving && (
                                        <>
                                        <span aria-hidden="true"> · </span>
                                        <span className={styles.sistEndret}>{t('admin.adminPage.lastEdited', { date: sistEndretKort })}</span>
                                        </>
                                    )}
                                </span>
                            </>
                        ) : (
                            <Button
                                variant="primary"
                                size="sm"
                                leftIcon={<FloppyDiskIcon size={16} />}
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Publiserer…' : 'Publiser'}
                            </Button>
                        ))}
                    </>
                }
            />

            <Toaster
                variant="success"
                icon={<CheckCircleIcon size={16} />}
                open={saveAlert === 'success'}
                onClose={() => setSaveAlert(null)}
                message={saveAlertMessage}
            />
            <Dialog
                open={unpublishDialogOpen}
                onClose={() => setUnpublishDialogOpen(false)}
                title="Avpubliser plan"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setUnpublishDialogOpen(false)} disabled={unpublishing}>
                            Avbryt
                        </Button>
                        <Button variant="danger" onClick={handleUnpublish} disabled={unpublishing}>
                            {unpublishing ? 'Avpubliserer…' : 'Avpubliser'}
                        </Button>
                    </>
                }
            >
                <p>Er du sikker på at du vil avpublisere planen? Planen vil ikke lenger være synlig i Drømmeplan.</p>
            </Dialog>
            <Toaster
                variant="danger"
                icon={<WarningCircleIcon size={16} />}
                open={saveAlert === 'error'}
                onClose={() => setSaveAlert(null)}
                autoCloseDuration={undefined}
                message="Noe gikk galt. Prøv igjen."
            />

            <div className={styles.body}>
                <div className={`${styles.navCollapser}${eiendomvelgerOpen ? ' ' + styles.navCollapserClosed : ''}`}>
                <aside className={styles.sidenav} aria-hidden={eiendomvelgerOpen || undefined}>
                    <NavPanel
                        planId={planId}
                        kommune={kommune}
                        areas={filteredAreas}
                        loading={areasLoading}
                        totalAreaCount={areas.length}
                        hiddenInPlan={hiddenInPlan}
                        egendefinerte={egendefinerte}
                        delområdegrupper={gruppeViews}
                        activeContext={context}
                        onSelect={handleSelect}
                        onToggleFilter={() => setFilterOpen((o) => !o)}
                        onClearPlanFilter={handleClearPlanFilter}
                        filterOpen={filterOpen}
                        onMoveOmråde={handleNavMoveOmråde}
                        onCopyVeiledninger={handleNavCopyVeiledninger}
                    />
                </aside>
                </div>

                {!eiendomvelgerOpen && filterOpen && (
                    <aside
                        id="delomradetype-filter-column"
                        className={styles.filterColumn}
                        aria-label="Filtrer delområdetyper"
                    >
                        <div className={styles.filterHeader}>
                            <h2 className={styles.filterTitle}>Filtrer delområdetyper</h2>
                            <IconButton
                                icon={<XIcon size={14} weight="bold" />}
                                label="Lukk filter"
                                variant="ghost"
                                size="sm"
                                onClick={() => setFilterOpen(false)}
                            />
                        </div>
                        <div className={styles.filterContent}>
                            <DelomradetypeFilterPanel areas={areas} />
                        </div>
                    </aside>
                )}

                <main className={styles.content}>
                    {dragBanner && (
                        <div className={styles.dragBanner}>
                            <Alert
                                variant="success"
                                icon={<CheckCircleIcon size={16} weight="fill" />}
                                open
                                onClose={() => setDragBanner(null)}
                                autoCloseDuration={5000}
                            >
                                <span className={styles.dragBannerContent}>
                                    <span>{dragBanner.message}</span>
                                    <Button variant="ghost" size="sm" onClick={() => { dragBanner.onUndo(); setDragBanner(null); }}>
                                        Angre
                                    </Button>
                                </span>
                            </Alert>
                        </div>
                    )}
                    {områdeEditorOpen ? (
                        <OmrådeEditorPanel
                            planId={planId}
                            initialOmråde={editingOmråde ?? undefined}
                            initialParent={creatingParent ?? undefined}
                            delområder={areas}
                            delområdegrupper={gruppeViews}
                            eiendommerUrl={eiendommerUrl}
                            onClose={closeOmrådeEditor}
                            onConverted={handleOmrådeConverted}
                            onMoved={handleOmrådeFlyttet}
                            onDeleted={() => setContext({ type: 'egendefinerte-admin', planId })}
                            onPublishAfterSave={() => {
                                if (isPublished) {
                                    handlePublishAfterSave().catch(console.error);
                                } else {
                                    handleEdited();
                                }
                            }}
                        />
                    ) : groupEditorOpen ? (
                        <GroupEditorPanel
                            key={groupEditor?.gruppe?.id ?? 'new'}
                            initial={groupEditor}
                            availableTypes={areas}
                            currentMembers={groupCurrentMembers}
                            blockedMembersByType={blockedMembersByType}
                            onSubmit={handleGroupSubmit}
                            onDelete={groupEditor?.gruppe ? handleGroupDelete : undefined}
                            onCancel={closeGroupEditor}
                            error={groupEditorError}
                        />
                    ) : eiendomvelgerOpen ? (
                        <EiendomsvelgerPanel
                            target={eiendomvelgerTarget}
                            loading={context.type === 'plan' && planGeometryQuery.isLoading}
                            eiendommerUrl={eiendommerUrl}
                            onClose={() => setEiendomvelgerOpen(false)}
                        />
                    ) : context.type === 'innstillinger' ? (
                        <InstillingerView
                            initialBeskrivelse={instillingerBeskrivelse}
                            initialPlanregisterlink={instillingerPlanregisterlink}
                            initialGjeldendeBestemmelser={instillingerGjeldendeBestemmelser}
                            onChangeBeskrivelse={setInstillingerBeskrivelse}
                            onChangePlanregisterlink={setInstillingerPlanregisterlink}
                            onChangeGjeldendeBestemmelser={setInstillingerGjeldendeBestemmelser}
                            onUpdateBeskrivelse={onUpdateBeskrivelse}
                            onUpdatePlanregisterlink={onUpdatePlanregisterlink}
                            onUpdateGjeldendeBestemmelser={onUpdateGjeldendeBestemmelser}
                        />
                    ) : context.type === 'delomrade-admin' ? (
                        <DelomradeAdmin
                            areas={filteredAreas}
                            grupper={gruppeViews}
                            onNewGroup={handleNewGroupGeneric}
                            onEditGroup={handleEditGroupFromList}
                        />
                    ) : context.type === 'egendefinerte-admin' ? (
                        <EgendefinerteOmraderAdmin
                            planId={planId}
                            egendefinerte={egendefinerte.filter(
                                (e) =>
                                    e.kind === 'egendefinert' &&
                                    e.parentType !== 'Delområde' &&
                                    e.parentType !== 'Delområdetype'
                            )}
                            delområder={areas}
                            onAdd={() => openOmrådeEditorForCreate()}
                            onEditOmråde={openOmrådeEditorForEdit}
                            onSelectOmråde={(item) => handleSelect({
                                type: 'omrade',
                                planId,
                                omradeId: item.id,
                                omradeNavn: item.navn,
                            })}
                        />
                    ) : (
                    <>
                    <SectionList
                        seksjoner={adminSections.seksjoner}
                        loading={adminSections.loading}
                        contextCode={contextCode(context, filteredAreas)}
                        contextLabel={contextLabel(context, areas, gruppeViews)}
                        contextSubtitle={contextSubtitle(
                            context,
                            planNavn,
                            activeAreaInfo?.label,
                            activeAreaInfo?.childCount,
                            context.type === 'delomradegruppe'
                                ? gruppeViews.find((g) => g.id === context.gruppeId)?.memberDelomradeIds.length
                                : undefined
                        )}
                        breadcrumb={context.type === 'delomrade' && context.delomradetype ? [
                            {
                                label: filteredAreas.find(a => a.id === context.delomradetype)?.navn ?? context.delomradetype,
                                onClick: () => handleSelect({ type: 'delomradetype', planId, delomradetype: context.delomradetype! }),
                            },
                        ] : undefined}
                        flatView={context.type === 'plan' || context.type === 'kommune'}
                        areaChildCount={activeAreaInfo?.childCount}
                        onAdd={(values) => adminSections.addSeksjon(values)}
                        onUpdate={(existing, values) =>
                            adminSections.updateSeksjon(String(existing.objectId), values)
                        }
                        onDelete={adminSections.deleteSeksjon}
                        onToggleOverride={handleToggleOverride}
                        onHide={handleHideSection}
                        onUnhide={handleUnhideSection}
                        onReorder={handleReorder}
                        onAddGroup={context.type === 'delomradetype' && context.delomradetype ? handleAddGroup : undefined}
                        onAddOmråde={context.type === 'delomrade' || context.type === 'omrade' ? openOmrådeEditorAsChild : undefined}
                        onEdit={canEditActiveContext ? handleEditActiveContext : undefined}
                        onEditEiendommer={
                            context.type === 'plan' ||
                            context.type === 'delomrade' ||
                            context.type === 'delomradegruppe'
                                ? () => setEiendomvelgerOpen(true)
                                : undefined
                        }
                        isPublished={isPublished}
                        onPublishAfterSave={isPublished ? handlePublishAfterSave : undefined}
                        importedGlobalIds={
                            importedSections?.targetContextId === contextId(context)
                                ? importedSections.globalIds
                                : undefined
                        }
                        kanMerkeUregulert={
                            context.type !== 'kommune' &&
                            (plan.planTypeKey === 'Kommuneplan' || plan.planTypeKey === 'Kommunedelplan')
                        }
                    />
                    </>
                    )}
                </main>

            </div>

            <HelpSheet open={showHelp} onClose={() => setShowHelp(false)} />

        </div>
    );
}
